import React from 'react';
import {Paper} from '../Paper/Paper';
import {Box} from '../Box/Box';
import {IconButton} from '../Button/IconButton';
import {usePopoverContext} from './PopoverContext';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import {styled} from '@mui/material/styles';
import FocusTrap from 'focus-trap-react';
import CloseIcon from '@mui/icons-material/Close';
import type {PopperProps} from '@mui/material/Popper';
import type {PaperProps} from '../Paper/Paper';

type FuelAlign = 'start' | 'end' | 'center';
type FuelPlacement = 'bottom' | 'bottom-start' | 'bottom-end' | 'top' | 'top-start' | 'top-end' | 'left' | 'left-start' | 'left-end' | 'right' | 'right-start' | 'right-end';

export interface PopoverContentProps {
    /**
     * If `true`, the popper will change its placement and flip when it's
     * about to overflow its boundary area.
     * @default true
     */
    flip?: boolean;
    /**
     * The placement of the popper relative to its reference.
     * @default "bottom-start"
     */
    placement?: FuelPlacement;
    /**
     * Array of popper.js modifiers. Check the docs to see
     * the list of possible modifiers you can pass.
     *
     * @see Docs https://popper.js.org/docs/v2/modifiers/
     */
    popperModifiers?: PopperProps['modifiers'];
    /* The content of the component */
    children: React.ReactNode;
    /**
     * Alignment of Popover in relation to button.
     * Note: This overrides the placement prop.
     */
    align?: FuelAlign;
    /**
     * Configuration options for FocusTrap component
     * https://github.com/focus-trap/focus-trap
     */
    focusTrapOptions?: Record<string, unknown>;
    /**
     * Properties applied to Paper component
     * used to wrap content.
     */
    PaperProps?: PaperProps;
}

const transforms = {
    top: 'bottom center',
    'top-start': 'bottom left',
    'top-end': 'bottom right',

    bottom: 'top center',
    'bottom-start': 'top left',
    'bottom-end': 'top right',

    left: 'right center',
    'left-start': 'right top',
    'left-end': 'right bottom',

    right: 'left center',
    'right-start': 'left top',
    'right-end': 'left bottom'
};

const StyledPopoverContent = styled(Popper)({
    zIndex: 1300
});

const getMenuAlignment = (align: FuelAlign): FuelPlacement => {
    switch (align) {
        case 'end':
            return 'bottom-end';
        case 'center':
            return 'bottom';
        default:
            return 'bottom-start';
    }
};

export const PopoverContent = React.forwardRef<any, PopoverContentProps>(
    (
        {
            children,
            placement = 'bottom-start',
            align,
            flip = true,
            focusTrapOptions,
            popperModifiers,
            PaperProps: paperProps,
            ...props
        },
        ref
    ) => {
        const resolvedPlacement = align ? getMenuAlignment(align) : placement;

        const {
            open,
            anchorRef,
            handleClose,
            handleKeyDown,
            showClose,
            popoverId
        } = usePopoverContext('Content');

        let modifiers: PopperProps['modifiers'] = [
            {
                name: 'flip',
                enabled: Boolean(flip)
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 4]
                }
            }
        ];

        if (popperModifiers) {
            modifiers = modifiers.concat(popperModifiers);
        }

        return (
            <StyledPopoverContent
                anchorEl={anchorRef?.current}
                disablePortal
                id={popoverId}
                modifiers={modifiers}
                open={open}
                placement={resolvedPlacement}
                ref={ref}
                role='dialog'
                transition
                {...props}
            >
                {({
                    TransitionProps, placement: childPlacement
                }): React.ReactElement => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: transforms[childPlacement as FuelPlacement]
                        }}
                    >
                        <Box>
                            <ClickAwayListener onClickAway={handleClose}>
                                <Paper
                                    borderRadius='md'
                                    elevation={8}
                                    onKeyDown={handleKeyDown}
                                    {...paperProps}
                                >
                                    <FocusTrap
                                        active={open}
                                        focusTrapOptions={{
                                            allowOutsideClick: true,
                                            preventScroll: true,
                                            ...focusTrapOptions
                                        }}
                                    >
                                        <Box>
                                            { showClose && <Box
                                                sx={{
                                                    position: 'absolute',
                                                    right: '8px',
                                                    top: '8px'
                                                }}
                                            >
                                                <IconButton
                                                    aria-label='close'
                                                    onClick={handleClose}
                                                    size='large'
                                                    style={{
                                                        height: '24px',
                                                        width: '24px'
                                                    }}
                                                >
                                                    <CloseIcon fontSize='small'/>
                                                </IconButton>
                                            </Box>
                                            }
                                            {children}
                                        </Box>
                                    </FocusTrap>
                                </Paper>
                            </ClickAwayListener>
                        </Box>
                    </Grow>
                )}
            </StyledPopoverContent>
        );
    });

PopoverContent.displayName = 'PopoverContent';
